<template>
  <div>
    <!-- Popup promotionnel -->
    <LoanPromoPopup v-if="showPromoPopup" @close="hidePromoPopup" />

    <!-- Bouton de demande de nouveau prêt -->
    <div class="text-xs-right" v-if="$store.state.lengthActiveLoans < 3">
      <v-btn
        class="white--text elevation-6"
        small
        round
        color="teal darken-2"
        :to="{name: 'loans.new_loan'}"
      >
        <v-icon left class="fas fa-plus"></v-icon>
        <span>Request New Loan</span>
      </v-btn>
    </div>
    <div class="text-xs-right" v-else>
      <v-btn
        class="white--text elevation-6"
        small
        round
        color="teal darken-2"
        @click="completeActiveLoanMsg"
      >
        <span>Request New Loan</span>
      </v-btn>
    </div>

    <!-- Message si aucun prêt -->
    <div v-if="loans.length === 0" class="text-xs-center pa-5">
      <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
      <h3 class="grey--text mt-3">No {{ $route.params.category }} loans yet</h3>
    </div>

    <!-- Total dû -->
    <v-card v-if="$store.state.lengthActiveLoans > 0" class="elevation-4 mt-2" style="border-radius: 10px">
      <v-layout row wrap text-xs-center>
        <h2 class="pa-2 dark--text text-xs-left">
          <span class="blue--text">Current Owing:</span> {{ formatAmount(currently_owing) }} FCFA
        </h2>
      </v-layout>
    </v-card>

    <!-- Liste des prêts -->
    <div class="mt-3 pa-0" style="max-height: 400px; overflow: auto">
      <v-hover v-for="item in loans" :key="item.id">
        <v-card
          slot-scope="{ hover }"
          :class="`elevation-${hover ? 18 : 4}`"
          color="white"
          :to="'/savings/loans/detail/'+item.id"
          class="mb-2 loan-card"
        >
          <v-card-text class="px-3 py-2" style="font-size: 12px; cursor: pointer">
            <!-- 🟦 PRÊT LEGACY -->
            <div v-if="item.is_legacy">
              <div class="d-flex align-center">
                <h2 :class="getTitleColor(item.unified_status)">
                  Loan: {{ item.name }}
                  <v-chip
                    v-if="item.cancelled"
                    x-small
                    color="error"
                    text-color="white"
                    class="ml-2"
                  >Cancelled</v-chip>
                  <v-chip
                    v-else-if="item.validated"
                    x-small
                    color="success"
                    text-color="white"
                    class="ml-2"
                  >Ongoing</v-chip>
                  <v-chip
                    v-else
                    x-small
                    color="grey"
                    text-color="white"
                    class="ml-2"
                  >Inactive</v-chip>
                  <blink v-if="item.notification" class="pt-0 mt-0" style="font-size: 8px">
                    <i class="red--text fas fa-circle"></i>
                  </blink>
                </h2>
              </div>
              <div><b>Creation Date:</b> {{ item.get_date_str }}</div>
              <div v-if="item.target_amount && item.target_date">
                <b>Objective:</b> Reach {{ formatAmount(item.target_amount) }} FCFA by {{ item.target_date }}
              </div>
              <div v-else-if="item.target_amount">
                <b>Objective:</b> Reach {{ formatAmount(item.target_amount) }} FCFA
              </div>
              <div v-else-if="item.target_date">
                <b>Objective:</b> On {{ item.target_date }}
              </div>
              <div class="mt-1">
                <v-progress-linear
                  :value="parsePercentage(item.percentage_completed)"
                  color="primary"
                  height="4"
                  rounded
                ></v-progress-linear>
                <span class="caption">{{ item.percentage_completed }} completed</span>
              </div>
            </div>

            <!-- 🟩 NOUVEAU PRÊT -->
            <div v-else>
              <div class="d-flex align-center">
                <h2 :class="getTitleColor(item.unified_status)">
                  Loan #{{ item.id }} - {{ formatAmount(item.loan_amount) }} FCFA
                  <v-chip
                    x-small
                    :color="getStatusChipColor(item.unified_status)"
                    text-color="white"
                    class="ml-2"
                  >{{ item.status_label }}</v-chip>
                </h2>
              </div>
              <div><b>Creation Date:</b> {{ formatDate(item.creation_date) }}</div>
              <div class="d-flex justify-space-between mt-1">
                <span><b>Total:</b> {{ formatAmount(item.total_amount) }} FCFA</span>
                <span><b>Paid:</b> {{ formatAmount(item.amount_paid) }} FCFA</span>
                <span><b>Remaining:</b> <span class="orange--text">{{ formatAmount(item.remaining_amount) }} FCFA</span></span>
              </div>
              <div class="mt-1">
                <v-progress-linear
                  :value="item.progress_percent"
                  color="primary"
                  height="4"
                  rounded
                ></v-progress-linear>
                <span class="caption">{{ item.progress_percent }}% repaid</span>
              </div>
              <div class="caption grey--text mt-1">
                Due: {{ item.due_date || '—' }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-hover>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import LoanPromoPopup from "@/layouts/LoanPromoPopup.vue"

export default {
  components: {
    LoanPromoPopup
  },

  data() {
    return {
      loans: [],
      currently_owing: 0,
      showPromoPopup: false
    }
  },

  computed: {
    loan_category() {
      return this.$route.params.category
    },

    currentUserId() {
      return this.$store.state.user_info?.user?.id || this.$store.state.username || 'anonymous'
    }
  },

  watch: {
    '$route': 'getLoans'
  },

  mounted() {
    this.getLoans()
    this.$store.commit('setCloseURL', "/savings")
    document.title = `PettyCash | ${this.loan_category} user's loans`
    this.checkAndShowPromoPopup()
  },

  methods: {
    async getLoans() {
      this.$store.commit('setIsLoading', true)
      const category = this.loan_category
      try {
        const response = await axios.get(`/api/v1/savings/get/user/loans/${category}/`)
        this.loans = response.data.loans || []
        this.currently_owing = response.data.currently_owing || 0
        this.$store.commit('setLengthActiveLoans', this.loans.filter(l =>
          ['APPROVED', 'DISBURSED', 'REPAYING'].includes(l.unified_status)
        ).length)
      } catch (error) {
        this.showError(error)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },

    checkAndShowPromoPopup() {
      const hasSeenPromo = this.$store.getters.loanPromoSeenForCurrentUser
      if (!hasSeenPromo) {
        this.showPromoPopup = true
      }
    },

    hidePromoPopup() {
      this.showPromoPopup = false
      this.$store.commit('setLoanPromoSeenForUser', { userId: this.currentUserId })
    },

    completeActiveLoanMsg() {
      this.$store.commit('setSnackBarMessage', "Your active loans have reached the maximum limit of 2, please complete them first.")
      this.$store.commit('setSnackBarColor', "red darken-4")
      this.$store.commit('activateSnackBar', true)
    },

    getTitleColor(status) {
      const colors = {
        'APPROVED': 'success--text',
        'DISBURSED': 'primary--text',
        'REPAYING': 'purple--text',
        'COMPLETED': 'success--text',
        'REJECTED': 'error--text',
        'PENDING': 'warning--text',
        'DEFAULTED': 'error--text'
      }
      return colors[status] || 'grey--text'
    },

    getStatusChipColor(status) {
      const colors = {
        'PENDING': 'orange',
        'APPROVED': 'success',
        'DISBURSED': 'primary',
        'REPAYING': 'purple',
        'COMPLETED': 'success',
        'REJECTED': 'error',
        'DEFAULTED': 'error'
      }
      return colors[status] || 'grey'
    },

    formatAmount(value) {
      if (!value && value !== 0) return '—'
      return new Intl.NumberFormat('fr-FR').format(value)
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-CM', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    parsePercentage(percentStr) {
      if (!percentStr) return 0
      const num = parseInt(percentStr.replace('%', ''))
      return isNaN(num) ? 0 : num
    },

    showError(error) {
      let msg = 'An error occurred, please check your connection.'
      if (error.response) {
        msg = error.response.data?.error || error.response.statusText
      }
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>

<style scoped>
.loan-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px !important;
}
.loan-card:hover {
  transform: translateY(-2px);
}
blink {
  -webkit-animation: 4s linear infinite condemned_blink_effect;
  animation: 4s linear infinite condemned_blink_effect;
}
@-webkit-keyframes condemned_blink_effect {
  0% { visibility: hidden; }
  50% { visibility: hidden; }
  100% { visibility: visible; }
}
@keyframes condemned_blink_effect {
  0% { visibility: hidden; }
  50% { visibility: hidden; }
  100% { visibility: visible; }
}
</style>